import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Button,
    Container,
    FilterBarToggle,
    Menu,
    MenuItem,
    MenuItems,
    MenuSeparator,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import { MenuButton } from '@headlessui/react';
import {
    ArchiveBoxArrowDownIcon,
    ArchiveBoxXMarkIcon,
    EllipsisVerticalIcon,
    PencilIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { Link, router, usePage } from '@inertiajs/react';

const IndexView = () => {
    const { auth, sector_parameters } = usePage().props;
    const { filters, updateFilters, isSorted } = useFilters([
        'sector_parameters',
    ]);
    const confirm = useConfirmationStore(state => state.confirm);

    const toggleArchive = sectorParameter => {
        router.post(
            `/sector-parameters/${sectorParameter.id}/${sectorParameter.archived_at ? 'restore' : 'archive'}`,
            {},
            { preserveScroll: true, preserveState: true }
        );
    };

    const destroy = sectorParameter => {
        router.delete(`/sector-parameters/${sectorParameter.id}`, {
            preserveScroll: true,
            preserveState: true,
        });
    };

    const confirmDelete = sectorParameter =>
        confirm({
            title: 'Parameter verwijderen?',
            description:
                'Weet je zeker dat je deze parameter wilt verwijderen? Alle jaarwaarden worden ook verwijderd en gekoppelde berekeningen zullen niet meer kloppen.',
            confirmLabel: 'Verwijderen',
            onConfirm: close => {
                destroy(sectorParameter);
                close();
            },
        });

    return (
        <Container>
            <Segment>
                <SegmentHeader
                    header={`Parameters (${sector_parameters?.meta?.total || 0})`}
                >
                    {auth?.permissions?.includes('create parameters') && (
                        <Button
                            variant='primary'
                            as={Link}
                            href='/sector-parameters/create'
                        >
                            <PlusIcon className='size-5' />
                            Nieuwe parameter
                        </Button>
                    )}
                </SegmentHeader>
                <SegmentContent>
                    <SearchBarFilter only={['sector_parameters']}>
                        <FilterBarToggle
                            label='Toon gearchiveerd'
                            checked={filters?.show_archived == 1}
                            onChange={checked =>
                                updateFilters(
                                    'show_archived',
                                    checked ? '1' : '0'
                                )
                            }
                        />
                    </SearchBarFilter>

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell
                                    sorted={isSorted('unique_id')}
                                    onClick={() =>
                                        updateFilters('sort', 'unique_id')
                                    }
                                >
                                    UUID
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted('name')}
                                    onClick={() =>
                                        updateFilters('sort', 'name')
                                    }
                                >
                                    Naam
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className='w-px'
                                    textAlign='center'
                                >
                                    Jaren
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className='w-px'
                                    textAlign='right'
                                />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sector_parameters?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={4} />
                            )}

                            {sector_parameters?.data?.map(sectorParameter => (
                                <TableRow
                                    key={sectorParameter.id}
                                    error={
                                        sectorParameter.missing_current_year_value
                                    }
                                >
                                    <TableCell singleLine>
                                        {sectorParameter.unique_id}
                                    </TableCell>
                                    <TableCell>
                                        {auth?.permissions?.includes(
                                            'update parameters'
                                        ) ? (
                                            <Link
                                                href={`/sector-parameters/${sectorParameter.id}/edit`}
                                            >
                                                {sectorParameter.name}
                                            </Link>
                                        ) : (
                                            sectorParameter.name
                                        )}
                                    </TableCell>
                                    <TableCell
                                        className='w-px'
                                        singleLine
                                        textAlign='center'
                                    >
                                        {sectorParameter.children_count}
                                    </TableCell>
                                    <TableCell
                                        className='w-px'
                                        singleLine
                                        textAlign='right'
                                    >
                                        {[
                                            'update parameters',
                                            'delete parameters',
                                        ]?.some(permission =>
                                            auth?.permissions?.includes(
                                                permission
                                            )
                                        ) && (
                                            <Menu>
                                                <MenuButton
                                                    aria-label={`Acties voor ${sectorParameter.name}`}
                                                >
                                                    <EllipsisVerticalIcon className='size-5' />
                                                </MenuButton>
                                                <MenuItems anchor='bottom end'>
                                                    {auth?.permissions?.includes(
                                                        'update parameters'
                                                    ) && (
                                                        <>
                                                            <MenuItem
                                                                as={Link}
                                                                href={`/sector-parameters/${sectorParameter.id}/edit`}
                                                                icon={
                                                                    PencilIcon
                                                                }
                                                            >
                                                                Wijzigen
                                                            </MenuItem>

                                                            <MenuSeparator />
                                                        </>
                                                    )}
                                                    {auth?.permissions?.includes(
                                                        'delete parameters'
                                                    ) && (
                                                        <>
                                                            <MenuItem
                                                                onClick={() =>
                                                                    toggleArchive(
                                                                        sectorParameter
                                                                    )
                                                                }
                                                                icon={
                                                                    sectorParameter.archived_at
                                                                        ? ArchiveBoxXMarkIcon
                                                                        : ArchiveBoxArrowDownIcon
                                                                }
                                                            >
                                                                {sectorParameter.archived_at
                                                                    ? 'Terugzetten'
                                                                    : 'Archiveren'}
                                                            </MenuItem>
                                                            <MenuItem
                                                                className='text-red-600'
                                                                onClick={() =>
                                                                    confirmDelete(
                                                                        sectorParameter
                                                                    )
                                                                }
                                                                icon={TrashIcon}
                                                            >
                                                                Verwijderen
                                                            </MenuItem>
                                                        </>
                                                    )}
                                                </MenuItems>
                                            </Menu>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>
                <SegmentFooter>
                    <CustomPagination
                        meta={sector_parameters?.meta}
                        only={['sector_parameters']}
                    />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default IndexView;
